import React from 'react';

import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import SortableTable from '~/components/core/Tables/SortableTable';
import useCurrencyFormatter from '~/components/CurrencyFormatterContext';
import { getNetAmount, renderVatDetailsCell } from '~/components/Finances/vatDetailsCell';
import { usePaymentsConfiguration } from '~/components/hooks/usePaymentsConfiguration';
import { useVatLabel } from '~/components/hooks/useVatLabel';
import RecoveryRequestsTable from '~/components/RecoveryRequests/RecoveryRequestsTable';
import { serverDateTimeToLocal } from '~/DateTimeUtils';
import type {
  ClaimFinanceRecoveriesResponse as RecoveriesResponse,
  ClaimFinanceRecoveriesResponseClaimRecoveryAmountRow as RecoveryAmountRow,
} from '~/server_shared/openapi/data-contracts';

import TEXTS from './texts';

const DisplayCurrency = ({ amount }: { amount: number }) => {
  const { currencyFormatter } = useCurrencyFormatter();
  return <>{currencyFormatter.format(amount)}</>;
};

const Capitalized = ({ children }: { children: React.ReactNode }) => {
  return <span className="capitalize">{children}</span>;
};

type DisplayRecoveryRow = Omit<RecoveryAmountRow, 'id'> & {
  id: number | string;
  sub_rows?: DisplayRecoveryRow[];
  is_group_summary?: boolean;
  is_group_allocation?: boolean;
};

// Split general recoveries (recovery_group_id set) collapse into one summary row with the
// allocations as sub_rows; single recoveries (null group id) stay flat, unchanged.
export const buildRecoveryDisplayRows = (recoveries: RecoveryAmountRow[]): DisplayRecoveryRow[] => {
  const result: DisplayRecoveryRow[] = [];
  const groupRowsById = new Map<string, RecoveryAmountRow[]>();
  const summaryIndexById = new Map<string, number>();

  recoveries.forEach((row) => {
    const groupId = row.recovery_group_id;
    if (!groupId) {
      result.push(row);
      return;
    }
    if (!groupRowsById.has(groupId)) {
      summaryIndexById.set(groupId, result.length);
      result.push({ ...row, id: `group-${groupId}`, is_group_summary: true, sub_rows: [] });
      groupRowsById.set(groupId, []);
    }
    groupRowsById.get(groupId)!.push(row);
  });

  groupRowsById.forEach((groupRows, groupId) => {
    const summary = result[summaryIndexById.get(groupId)!];
    summary.amount = groupRows.reduce((total, alloc) => total + alloc.amount, 0);
    // sorted, as the transaction-history and note labels for the same group are: the API orders
    // allocations only by recovery date, which every row of one action shares
    summary.payable_type = [...new Set(groupRows.map((alloc) => alloc.payable_type).filter(Boolean))].sort().join(', ');
    summary.general_recovery_type = null;
    summary.general_recovery_type_desc = `${groupRows.length} ${groupRows.length === 1 ? 'allocation' : 'allocations'}`;
    // each allocation carries its own VAT, so the summary reports the group's total. A group whose
    // allocations were all recorded without the VAT question stays null ("unknown"), which the cell
    // renders as '-'; collapsing that to false would claim an answer nobody gave.
    summary.is_vat_included = groupRows.some(
      (alloc) => alloc.is_vat_included !== null && alloc.is_vat_included !== undefined
    )
      ? groupRows.some((alloc) => alloc.is_vat_included)
      : null;
    summary.vat_amount = groupRows.reduce<number | null>(
      (total, alloc) =>
        alloc.vat_amount === null || alloc.vat_amount === undefined ? total : (total ?? 0) + alloc.vat_amount,
      null
    );
    // allocations keep their own amount, recovery type and VAT; the action-level columns are the
    // summary row's to show, so they stay blank on the sub-rows
    summary.sub_rows = groupRows.map((alloc) => ({
      ...alloc,
      is_group_allocation: true,
      recovery_date: null,
      exposure_label: null,
      issued_by: null,
      recovered_from: null,
      reference: null,
      note: null,
    }));
  });

  return result;
};

const RecoveriesTab = ({
  data,
  isLoading,
  isError,
}: {
  data?: RecoveriesResponse;
  isLoading: boolean;
  isError: boolean;
}) => {
  const { includeVatInPaymentScreen, displayNetAmounts } = usePaymentsConfiguration();
  const vatLabel = useVatLabel();

  // Which figure the Amount column shows, mirroring the Payments tab: net (excl. VAT/GST) once the
  // toggle is on, and the gross amount recorded otherwise. A row recorded without VAT/GST has nothing
  // to subtract, so its net equals its gross. Group summary rows total both their allocations'
  // amounts and their VAT, so they net the same way.
  const getDisplayedAmount = (row: DisplayRecoveryRow): number =>
    (displayNetAmounts && row.is_vat_included ? getNetAmount(row.amount, row.vat_amount) : null) ?? row.amount;

  const recoveries = data?.recoveries;

  const displayRows = React.useMemo(() => buildRecoveryDisplayRows(recoveries ?? []), [recoveries]);
  const parentRowIds = React.useMemo(
    () => displayRows.filter((r) => r.is_group_summary).map((r) => r.id),
    [displayRows]
  );

  // the backend resolves the configured description from the org's recovery-type options
  const getRecoveryTypeDesc = (row: DisplayRecoveryRow): React.ReactNode => {
    if (row.is_group_summary) {
      return row.general_recovery_type_desc ?? '';
    }
    if (row.recovery_type !== 'general') {
      return '-';
    }
    if (!row.general_recovery_type) {
      return '';
    }
    return row.general_recovery_type_desc ?? row.general_recovery_type;
  };

  const columns = [
    {
      id: 'recovery_date',
      label: TEXTS.date,
      specialCell: (row: DisplayRecoveryRow) =>
        row.is_group_allocation ? '' : row.recovery_date ? serverDateTimeToLocal(row.recovery_date) : '-',
    },
    {
      id: 'recovery_type',
      // this column holds general/subro/salvage; when the configured recovery type of a general
      // recovery gets its own column below, that one takes the 'Recovery Type' header
      label: data?.includes_recovery_types ? TEXTS.recoverySource : TEXTS.recoveryType,
      disableSort: true,
      specialCell: (row: DisplayRecoveryRow) =>
        row.is_group_allocation ? '' : <Capitalized>{row.recovery_type}</Capitalized>,
    },
    {
      id: 'exposure_label',
      label: TEXTS.exposure,
      disableSort: true,
    },
    {
      id: 'issued_by',
      label: TEXTS.issuedBy,
      disableSort: true,
      specialCell: (row: DisplayRecoveryRow) => (row.is_group_allocation ? '' : row.issued_by || '-'),
    },
    {
      id: 'amount',
      label: displayNetAmounts ? TEXTS.netAmount : TEXTS.amount,
      // sorted on the figure the column actually shows, so the order matches what is read
      specialCmpFunc: (row1: DisplayRecoveryRow, row2: DisplayRecoveryRow) =>
        getDisplayedAmount(row1) - getDisplayedAmount(row2),
      specialCell: (row: DisplayRecoveryRow) => <DisplayCurrency amount={getDisplayedAmount(row)} />,
    },
    {
      id: 'recovered_from',
      label: TEXTS.recoveredFrom,
      disableSort: true,
      specialCell: (row: DisplayRecoveryRow) => (row.is_group_allocation ? '' : row.recovered_from || '-'),
    },
    {
      id: 'payable_type',
      label: TEXTS.payableType,
      disableSort: true,
      specialCell: (row: DisplayRecoveryRow) => <Capitalized>{row.payable_type}</Capitalized>,
    },
    // recovery type predates split recoveries, so the backend gates the column on the split recovery
    // setting rather than on a row carrying one; without it the table keeps its pre-split shape
    ...(data?.includes_recovery_types
      ? [
          {
            id: 'general_recovery_type',
            label: TEXTS.recoveryType,
            disableSort: true,
            specialCell: getRecoveryTypeDesc,
          },
        ]
      : []),
    {
      id: 'reference',
      label: TEXTS.reference,
      disableSort: true,
    },
    {
      id: 'note',
      label: TEXTS.note,
      disableSort: true,
    },
    {
      id: 'vat_details',
      label: `${vatLabel} details`,
      disableSort: true,
      isHidden: !includeVatInPaymentScreen,
      // every row renders its own VAT, allocations included: their VAT is per allocation now, and
      // the summary row above them shows the group's total
      specialCell: (row: DisplayRecoveryRow) =>
        renderVatDetailsCell({
          isVatIncluded: row.is_vat_included,
          vatAmount: row.vat_amount,
          currency: row.policy_currency,
          amount: row.amount,
          // The Amount column shows net once the toggle is on, so this cell switches to Gross + VAT,
          // exactly as the Payments tab's does.
          showGrossInsteadOfNet: displayNetAmounts,
        }),
    },
  ];

  const visibleColumnCount = columns.filter((c) => !c.isHidden).length;

  return (
    <div className="pb-8 pt-20">
      {/* The requests the money in the table below settles, read here rather than in
          FinancesScreen: InnerTabs mounts only the active tab, so this is one read per
          Recoveries-tab open instead of one per Finances-screen open. */}
      <RecoveryRequestsTable />
      {isLoading || isError ? (
        <SkeletonTable rowsCount={5} columnsCount={visibleColumnCount} isError={isError} />
      ) : (
        <SortableTable
          rows={displayRows as unknown as RecoveryAmountRow[]}
          columns={columns}
          subRowsPropertyName="sub_rows"
          checkIfRowIsExpandable={(row: DisplayRecoveryRow) => !!row.sub_rows?.length}
          boldRowsIds={parentRowIds}
          defaultOrderColumn={0}
          order="desc"
        />
      )}
    </div>
  );
};

export default RecoveriesTab;
